import { safeShareReturn } from '@/features/sharing/types'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router'

import { useRegister } from '@/api/hooks'
import { Button, Input } from '@/components/ui'

import { AuthErrorBanner } from './AuthErrorBanner'
import { AuthLayout } from './AuthLayout'
import { fieldErrorOf, generalErrorOf } from './authErrors'
import { PasswordInput } from './PasswordInput'

/** Inputs that render their own inline error, so the banner does not repeat them. */
const HANDLED_FIELDS = ['email', 'display_name', 'password', 'invite_code'] as const

export function RegisterPage() {
  const register = useRegister()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')

  const error = register.isError ? register.error : null
  const bannerMessage = generalErrorOf(error, HANDLED_FIELDS)
  const canSubmit =
    email.trim().length > 0 && displayName.trim().length > 0 && password.length > 0

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit || register.isPending) return

    register.mutate({
      email: email.trim(),
      display_name: displayName.trim(),
      password,
      // Undefined is dropped by JSON.stringify, so an open server never receives a
      // blank invite code it would then have to treat as an attempt.
      invite_code: inviteCode.trim() || undefined,
    })
  }

  if (register.isSuccess) return <Navigate to={safeShareReturn(location.state?.from)} replace />

  return (
    <AuthLayout
      title="Create your account"
      subtitle="One account per person on this server."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to="/login" state={{ from: safeShareReturn(location.state?.from) }}
            className="inline-flex min-h-11 items-center px-1 font-medium text-accent hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {bannerMessage ? <AuthErrorBanner message={bannerMessage} /> : null}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          disabled={register.isPending}
          error={fieldErrorOf(error, 'email')}
        />

        <Input
          label="Display name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Alex"
          autoComplete="name"
          maxLength={120}
          required
          disabled={register.isPending}
          error={fieldErrorOf(error, 'display_name')}
        />

        <PasswordInput
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          hint="At least 10 characters. Avoid common passwords."
          required
          disabled={register.isPending}
          error={fieldErrorOf(error, 'password')}
        />

        <Input
          label="Invite code"
          value={inviteCode}
          onChange={(event) => setInviteCode(event.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          hint="Only needed if this server is invite-only."
          disabled={register.isPending}
          error={fieldErrorOf(error, 'invite_code')}
        />

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={register.isPending}
          disabled={!canSubmit}
          className="mt-1"
        >
          Create account
        </Button>
      </form>
    </AuthLayout>
  )
}
